import type { Option } from "@tento-lona/sheets";
import { LonaWebComponent, html } from "@tento-ui/component";
import { Center, Column, Div } from "@tento-ui/component-builtin";
import { DomUtils } from "@tento-ui/dom";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { Anchor, AnchorInstance } from "@tento-ui/components/anchor";
import { ListCellLayout } from "@tento-ui/components/list/list-cell-layout";
import { PillButton } from "@tento-ui/components/pill-button";
import { Position } from "@tento-ui/anchor-positioning-utils";
import { Round } from "@tento-ui/components/round";
import { RenderPlugin } from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { $jsx } from "@tento-ui/ui/jsx";
import { Typography } from "@tento-ui/ui/typography";
import { LinkIcon, LonaIcons, ThreeDotsIcon } from "@tento-ui/ui/icons";

/** The dropdown that picks a new row's type, with a preview of each. */
@component({
  name: "lona-new-row-dropdown",
})
export class NewRowDropdown extends LonaWebComponent {
  $root: HTMLElement = this.$("root");
  $rowPreview: HTMLElement = this.$("row-preview");
  $rowPreviewImage: HTMLElement = this.$("row-preview-image");
  $rowPreviewCaption: HTMLElement = this.$("row-preview-caption");

  static makeWith(
    previews: RenderPlugin.Preview[],
    debugPreviews: RenderPlugin.Preview[],
    onCreateRowWithType: (type: string) => void,
    onLinkExistingRow: Option<() => void>,
    onFloatingButtonClick: Option<(preview: RenderPlugin.Preview) => void>,
  ): NewRowDropdown {
    const $e = NewRowDropdown.make();
    $e.bind(
      previews,
      debugPreviews,
      onCreateRowWithType,
      onLinkExistingRow,
      onFloatingButtonClick,
    );
    return $e;
  }

  static showPopover(
    previews: RenderPlugin.Preview[],
    debugPreviews: RenderPlugin.Preview[],
    $anchor: HTMLElement,
    onCreateRowWithType: (type: string) => void,
    onLinkExistingRow: Option<() => void>,
    onFloatingButtonClick: Option<(preview: RenderPlugin.Preview) => void>,
  ) {
    let instance: AnchorInstance;

    const $dropdown = NewRowDropdown.makeWith(
      previews,
      debugPreviews,
      (id) => {
        onCreateRowWithType(id);
        instance.dismiss();
      },
      onLinkExistingRow
        ? () => {
            instance.dismiss();
            onLinkExistingRow();
          }
        : null,
      onFloatingButtonClick
        ? (preview) => {
            instance.dismiss();
            onFloatingButtonClick(preview);
          }
        : null,
    );
    // NewRowDropdown already has max-height and overflow in its styles

    const $container = Round.makeWithContent($dropdown, {
      bg: "var(--bg-elevated)",
      radius: 8,
      shadow: "var(--box-shadow)",
    }).assignStyles({
      height: "fit-content",
    });

    $container.assignStyles({ width: "280px" });
    instance = Anchor.reveal({
      $anchor: $anchor,
      $content: $container,
      position: Position.TOP,
      offset: {
        outer: {
          vertical: 4,
          horizontal: 0,
        },
      },
    });
  }

  #tooltipHide: Option<ReturnType<typeof setTimeout>> = null;
  #floatingButtons: HTMLElement[] = [];

  #cleanupChildren() {
    for (const $fb of this.#floatingButtons) {
      GESTURE_MANAGER.unregister($fb);
    }
    this.#floatingButtons = [];
    for (const $child of this.$root.children) {
      GESTURE_MANAGER.unregister($child as HTMLElement);
      ($child as HTMLElement).onpointerenter = null;
      ($child as HTMLElement).onpointerleave = null;
    }
  }

  bind(
    previews: RenderPlugin.Preview[],
    debugPreviews: RenderPlugin.Preview[],
    completion: (id: string) => void,
    onLinkExistingRow: Option<() => void>,
    onFloatingButtonClick: Option<(preview: RenderPlugin.Preview) => void>,
  ) {
    this.#cleanupChildren();
    DomUtils.clearChildren(this.$root);
    const showPreview = (preview: RenderPlugin.Preview) => {
      if (this.#tooltipHide) {
        clearTimeout(this.#tooltipHide);
        this.#tooltipHide = null;
      }
      this.$rowPreview.style.opacity = "1";
      this.$rowPreviewImage.style.backgroundImage = `url("${preview.previewImageUrl}")`;
      this.$rowPreviewCaption.textContent = preview.description;
    };
    const hidePreview = () => {
      if (this.#tooltipHide) return;
      // this.#tooltipHide = setTimeout(() => {
      //   this.$rowPreview.style.opacity = "0";
      //   this.#tooltipHide = null;
      // }, 150);
    };

    for (const preview of previews) {
      const { $cell, $floatingButton } = NewRowDropdown.makePreview(
        preview,
        completion,
        "standard",
        onFloatingButtonClick,
      );
      if ($floatingButton) this.#floatingButtons.push($floatingButton);
      $cell.onpointerenter = () => showPreview(preview);
      $cell.onpointerleave = () => hidePreview();
      this.$root.appendChild($cell);
    }

    for (const preview of debugPreviews) {
      const { $cell, $floatingButton } = NewRowDropdown.makePreview(
        preview,
        completion,
        "debug",
        onFloatingButtonClick,
      );
      if ($floatingButton) this.#floatingButtons.push($floatingButton);
      $cell.onpointerenter = () => showPreview(preview);
      $cell.onpointerleave = () => hidePreview();
      this.$root.appendChild($cell);
    }

    if (onLinkExistingRow) {
      const $linkExistingRow =
        NewRowDropdown.makeLinkExistingRow(onLinkExistingRow);
      this.$root.appendChild($linkExistingRow);
    }
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        color: var(--color);
        display: block;
      }

      #root {
        overflow-y: scroll;
        height: 100%;
        max-height: 320px;
        padding: 4px;
      }

      #row-preview {
        position: absolute;
        top: 0;
        left: calc(100% + 8px);
        height: 320px;
        width: 276px;
        background: black;
        border-radius: 8px;
        padding: 8px;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.15s ease;
      }

      #row-preview-image {
        width: 100%;
        height: 260px;
        margin-bottom: 8px;
        border-radius: 4px;
        background-size: cover;
      }

      #row-preview-caption {
        color: white;
        --line-height-ratio: 1.2;
        --margin-top: 4px;
        --margin-bottom: 4px;
      }

      #row-preview-caption,
      caption {
        text-align: start;
        text-wrap: wrap;
      }

      .preview-row {
        cursor: pointer;
      }

      .preview-row:hover .floating-button {
        opacity: 1;
      }

      .floating-button {
        position: absolute;
        right: 8px;
        top: 50%;
        transform: translateY(-50%);
        width: 28px;
        height: 28px;
        border-radius: 50%;
        color: var(--fg-icon-ter);
        display: flex;
        align-items: center;
        transition: box-shadow 0.3s ease, background 0.3s ease;
        box-shadow: var(--box-shadow);
        background: var(--bg);
        opacity: 0;
      }

      .floating-button:hover {
        background: var(--bg-hover-opaque);
        box-shadow: none;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-col id="root"></std-col>
    <std-col id="row-preview">
      <div id="row-preview-image"></div>
      <std-col style="height:36px">
        <p id="row-preview-caption"></p>
      </std-col>
    </std-col>
  `;
}

/** Values that go with `NewRowDropdown`: `makeLinkExistingRow`, `makePreview`. */
export namespace NewRowDropdown {
  export function makeLinkExistingRow(completion: () => void): HTMLElement {
    const $content = Column.make();
    $content.style.alignItems = "start";
    $content.style.marginBlock = "3px";

    const $title = Typography.p("Link existing row");
    $content.appendChild($title);
    $content.appendChild(
      ListCellLayout.$caption("Connect a row like Calendar or Weather"),
    );

    const $cell = ListCellLayout.makeWith({
      $icon: $jsx(
        "std-center",
        {
          style: {
            height: "16px",
            width: "16px",
            marginRight: "12px",
            color: "var(--fg-icon-sec)",
            flexShrink: "0",
          },
        },
        LonaIcons.$svg(LinkIcon),
      ),
      $content,
    });

    $cell.addPointerEvent({
      onClick: completion,
    });

    return $cell;
  }

  export function makePreview(
    preview: RenderPlugin.Preview,
    completion: (id: string) => void,
    /** `"debug"` marks a debug-only row type with a Debug pill. */
    kind: "standard" | "debug",
    onFloatingButtonClick: Option<(preview: RenderPlugin.Preview) => void>,
  ): { $cell: HTMLElement; $floatingButton: Option<HTMLElement> } {
    const $content = Column.make();
    $content.style.alignItems = "start";
    $content.style.marginBlock = "3px";

    const $title = Typography.p(preview.label);
    $title.assignStyles({
      position: "relative",
    });
    if (kind === "debug")
      $title.appendChild(
        PillButton.makeWith("Debug", {
          background: "var(--purple)",
        }).assignStyles({
          position: "absolute",
          left: "calc(100% + 6px)",
          top: "50%",
          transform: "translateY(-50%)",
          "--pill-button-font-size": "var(--text-8px)",
          "--pill-button-padding": "3px 4px",
        }),
      );

    $content.appendChild($title);
    $content.appendChild(ListCellLayout.$caption(preview.description));

    const $cell = ListCellLayout.makeWith({
      $icon: $jsx(
        "std-center",
        {
          style: {
            height: "16px",
            width: "16px",
            marginRight: "12px",
            // Alias / integration rows get a purple icon so they read as
            // distinct from plain cell and layout rows in the palette.
            color: preview.isAlias ? "var(--purple-fg)" : "var(--fg-icon-sec)",
            flexShrink: "0",
          },
        },
        preview.icon.content.cloneNode(true) as HTMLElement,
      ),
      $content,
    }).addPointerEvent({
      onClick: () => completion(preview.id),
    });

    if (!onFloatingButtonClick) return { $cell, $floatingButton: null };

    const $fb = Center.make();
    $fb.classList.add("floating-button");
    $fb.appendChild(
      LonaIcons.make(ThreeDotsIcon, {
        sizePx: 16,
      }),
    );
    GESTURE_MANAGER.addPointerEvent($fb, {
      onClick: () => onFloatingButtonClick(preview),
    });

    const $c = Div.make().assignStyles({
      position: "relative",
    });
    $c.classList.add("preview-row");
    $c.appendChild($cell);
    $c.appendChild($fb);
    return { $cell: $c, $floatingButton: $fb };
  }
}
